/**
 * Recipient Search Hooks
 *
 * React Query hooks for the recipient search API endpoints.
 * Uses cursor-based pagination for efficient infinite scroll.
 */
import { useMemo } from 'react'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { useAuthInfo } from '@sweetauth/core'

import { API_URL } from '@/constants/env'

import type {
  IdentifierType,
  RecipientAutocompleteResult,
  RecipientCountResponse,
  RecipientSearchFilters,
  RecipientSearchResponse,
  RecipientSearchResult,
} from '@/types/recipient-search'

// --- Query Keys ---

export const recipientSearchKeys = {
  all: ['recipient-search'] as const,
  search: (filters: Partial<RecipientSearchFilters>) =>
    [...recipientSearchKeys.all, 'search', filters] as const,
  autocomplete: (query: string) =>
    [...recipientSearchKeys.all, 'autocomplete', query] as const,
  count: (filters: Partial<RecipientSearchFilters>) =>
    [...recipientSearchKeys.all, 'count', filters] as const,
}

// --- API Functions ---

async function fetchRecipients(
  token: string,
  filters: Partial<RecipientSearchFilters>,
  cursor?: string,
  personalized: boolean = true,
): Promise<RecipientSearchResponse> {
  const res = await fetch(`${API_URL}/api/v1/recipients/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ ...filters, cursor, personalized }),
  })

  if (!res.ok) {
    throw new Error(`Failed to fetch recipients: ${res.status}`)
  }

  return res.json()
}

async function fetchRecipientAutocomplete(
  token: string,
  query: string,
  limit: number = 10,
  parentsOnly: boolean = false,
): Promise<RecipientAutocompleteResult[]> {
  const params = new URLSearchParams({
    query,
    limit: String(limit),
  })
  if (parentsOnly) {
    params.set('parents_only', 'true')
  }

  const res = await fetch(
    `${API_URL}/api/v1/recipients/autocomplete?${params}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  )

  if (!res.ok) {
    throw new Error(`Failed to fetch autocomplete: ${res.status}`)
  }

  return res.json()
}

async function fetchRecipientCount(
  token: string,
  filters: Partial<RecipientSearchFilters>,
): Promise<RecipientCountResponse> {
  const res = await fetch(`${API_URL}/api/v1/recipients/count`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(filters),
  })

  if (!res.ok) {
    throw new Error(`Failed to fetch count: ${res.status}`)
  }

  return res.json()
}

// --- Hooks ---

/**
 * Search recipients with filters (single page)
 */
export function useRecipientSearch(
  filters: Partial<RecipientSearchFilters>,
  options: { enabled?: boolean; personalized?: boolean } = {},
) {
  const { enabled = true, personalized = true } = options
  const { accessToken } = useAuthInfo()

  return useQuery({
    queryKey: recipientSearchKeys.search(filters),
    queryFn: () => {
      if (!accessToken) throw new Error('No access token')
      return fetchRecipients(accessToken, filters, undefined, personalized)
    },
    enabled: enabled && !!accessToken,
    staleTime: 30 * 1000, // 30 seconds
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Infinite scroll search for recipients using cursor-based pagination
 */
export function useInfiniteRecipientSearch(
  filters: Omit<Partial<RecipientSearchFilters>, 'cursor'>,
  options: { enabled?: boolean; personalized?: boolean } = {},
) {
  const { enabled = true, personalized = true } = options
  const { accessToken } = useAuthInfo()

  return useInfiniteQuery({
    queryKey: recipientSearchKeys.search(filters),
    queryFn: ({ pageParam }) => {
      if (!accessToken) throw new Error('No access token')
      return fetchRecipients(
        accessToken,
        filters,
        pageParam ?? undefined,
        personalized,
      )
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      // Return next_cursor if there are more results, otherwise undefined to stop
      return lastPage.has_more ? lastPage.next_cursor : undefined
    },
    enabled: enabled && !!accessToken,
    staleTime: 30 * 1000,
    // Preserve previous results while fetching new data to avoid jarring skeleton flash
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Fast type-ahead autocomplete for recipient names
 */
export function useRecipientAutocomplete(
  query: string,
  options: { enabled?: boolean; limit?: number; parentsOnly?: boolean } = {},
) {
  const { enabled = true, limit = 10, parentsOnly = false } = options
  const { accessToken } = useAuthInfo()

  return useQuery({
    queryKey: [...recipientSearchKeys.autocomplete(query), { parentsOnly }],
    queryFn: () => {
      if (!accessToken) throw new Error('No access token')
      return fetchRecipientAutocomplete(accessToken, query, limit, parentsOnly)
    },
    enabled: enabled && !!accessToken && query.length >= 2,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Get exact count of recipients matching filters
 */
export function useRecipientCount(
  filters: Partial<RecipientSearchFilters>,
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options
  const { accessToken } = useAuthInfo()

  return useQuery({
    queryKey: recipientSearchKeys.count(filters),
    queryFn: () => {
      if (!accessToken) throw new Error('No access token')
      return fetchRecipientCount(accessToken, filters)
    },
    enabled: enabled && !!accessToken,
    staleTime: 60 * 1000, // 1 minute
    // Keep previous count during transitions - if count was 0, stay at 0
    // This prevents stale results from flashing when going empty → empty
    placeholderData: (previousData) => previousData,
  })
}

/**
 * Auto-lookup for identifier patterns (UEI, DUNS, CAGE)
 *
 * Fires an exact-match search when the query matches an identifier pattern.
 * Uses the specific filter field (uei, duns, cage_code) — not identifier_type
 * (which is prefix-match). Returns a single result or null.
 *
 * Unfiltered — no state/cert/NAICS filters applied. Purpose is
 * "does this exact ID exist?" regardless of current filter context.
 */
export function useIdentifierLookup(
  identifierType: IdentifierType | null,
  value: string,
) {
  const { accessToken } = useAuthInfo()
  const trimmedValue = value.trim()

  const exactFilters = useMemo((): Partial<RecipientSearchFilters> | null => {
    if (!identifierType || !trimmedValue) return null

    const filterField: Record<IdentifierType, string> = {
      uei: 'uei',
      duns: 'duns',
      cage: 'cage_code',
    }

    return {
      [filterField[identifierType]]: trimmedValue.toUpperCase(),
      limit: 1,
    }
  }, [identifierType, trimmedValue])

  return useQuery<RecipientSearchResponse, Error, RecipientSearchResult | null>(
    {
      queryKey: [
        ...recipientSearchKeys.all,
        'identifier-lookup',
        identifierType,
        trimmedValue,
      ],
      queryFn: () => {
        if (!accessToken || !exactFilters)
          throw new Error('Missing token or filters')
        return fetchRecipients(accessToken, exactFilters, undefined, false)
      },
      enabled: !!accessToken && !!exactFilters,
      staleTime: 60 * 1000,
      select: (data) => data.results[0] ?? null,
    },
  )
}
